// Rendering: canvas, isometric camera (fit, P, pan & zoom) and resize.
import { N } from '../config.js';
import { cv, wrap } from '../dom.js';

let ctx=cv.getContext('2d');
let W=0,H=0,ts=20,ox=0,oy=0;

/* ===== View: isometric camera, pan & zoom ===== */
let zoom=2,panX=0,panY=0,tw=32,th=16,zs=20,DPR=1;
const ISL=2.6; // island margin around the grid
function fit(){
  const span=N+ISL*2;const base=Math.min(W/(span*0.98),2*H/(span+5));
  tw=base*zoom;th=tw/2;zs=tw*.62;
  // pan limit: the screen centre must stay over the island. (It used to shrink with the zoom, so zooming in or out around
  // a point away from the middle hit the limit mid-gesture and the land jumped toward the centre.)
  const mx=span*tw/2*.85,my=span*th/2*.85+th*3;
  panX=Math.max(-mx,Math.min(mx,panX));panY=Math.max(-my,Math.min(my,panY));
  ox=W/2+panX;oy=H/2-N*th/2+th*2.4+panY;
}
const P=(x,y,z=0)=>[ox+(x-y)*tw/2,oy+(x+y)*th/2-z*zs];
// Could something standing on tiles x..x+s, y..y+s and h tiles tall show on screen? (generous margins: flags, glows, shake)
// Off-screen buildings, trees and rocks are skipped — on a zoomed-in phone that is often half the village.
function onScreen(x,y,s=1,h=2){const cx=ox+(x-y)*tw/2,cy=oy+(x+y+s)*th/2,m=tw*(s*.6+1.2);return cx>-m&&cx<W+m&&cy+th*s/2+m>0&&cy-zs*(h+1.5)-m<H}
function toWorld(e){const r=cv.getBoundingClientRect();const a=(e.clientX-r.left-ox)/(tw/2),b=(e.clientY-r.top-oy)/(th/2);return{x:(a+b)/2,y:(b-a)/2}}
function zoomAt(f,sx,sy){const r=cv.getBoundingClientRect();const a=(sx-ox)/(tw/2),b=(sy-oy)/(th/2);const wx=(a+b)/2,wy=(b-a)/2;
  zoom=Math.max(.8,Math.min(5,zoom*f));fit();const[nx,ny]=P(wx,wy);panX+=sx-nx;panY+=sy-ny;fit()}

/* ===== Render primitives ===== */
// Phones in portrait start zoomed in (the whole island would be tiny); pinch or the ⤢ button to change.
let zoomInit=false;const defaultZoom=()=>W<600&&H>W?2.7:2;
// dprCap: the highest pixel ratio the canvas renders at. The game lowers it on devices that can't keep up (see main.js).
// Phones and tablets (touch screens) go up to 2.5: their screens are 2.6–3× and anything lower is stretched and looks soft
// (players noticed at 1.75 and 2). Phones that can't keep up are stepped down by adaptQuality in main.js.
const MAX_DPR=typeof matchMedia==='function'&&matchMedia('(pointer: coarse)').matches?2.5:2;
let dprCap=MAX_DPR;
// Resizing the canvas wipes it. Without a redraw right away the browser shows that blank canvas (the light page background)
// until the next frame: a short white flash whenever the quality changed or the window resized. main.js sets the redraw.
let afterResize=null;export function setAfterResize(fn){afterResize=fn}
function resize(){const r=wrap.getBoundingClientRect();DPR=Math.min(dprCap,window.devicePixelRatio||1);cv.width=Math.round(r.width*DPR);cv.height=Math.round(r.height*DPR);ctx.setTransform(DPR,0,0,DPR,0,0);W=r.width;H=r.height;if(!zoomInit&&W>0){zoomInit=true;zoom=defaultZoom()}fit()
  if(afterResize)try{afterResize()}catch(e){}}
new ResizeObserver(resize).observe(wrap);resize();

/** Pan the camera so world point (x, y) sits in the middle of the screen. */
function centerOn(x,y,z=0){fit();const[sx,sy]=P(x,y,z);panX+=W/2-sx;panY+=H/2-sy;fit()}

// Temporarily point the camera and canvas somewhere else (used to paint troop portraits off-screen).
function withView(v,fn){const o={ctx,ox,oy,tw,th,zs};ctx=v.ctx;ox=v.ox;oy=v.oy;tw=v.tw;th=tw/2;zs=tw*.62;try{fn()}finally{ctx=o.ctx;ox=o.ox;oy=o.oy;tw=o.tw;th=o.th;zs=o.zs}}

// Setters so other modules can reassign these bindings (ES module imports are read-only).
export function setCtx(v){ctx=v}
export function setZoom(v){zoom=v}
export function setPanX(v){panX=v}
export function setPanY(v){panY=v}
export function setDprCap(v){dprCap=v;resize()}
export const getDprCap=()=>dprCap;
export { MAX_DPR };

export { centerOn, defaultZoom, withView, ctx, W, H, ox, oy, tw, th, zs, DPR, zoom, panX, panY, ISL, fit, P, onScreen, toWorld, zoomAt };
